import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// ── Route-chunk resilience (T67) ────────────────────────────────────────────
// Every route is React.lazy: the page module is a hashed chunk fetched on
// demand. Each deploy replaces the whole chunk set, so a tab opened BEFORE a
// deploy holds a stale index.html and 404s on the next chunk it lazy-loads —
// the import rejects and the app dead-ends on the error screen until a manual
// reload. This helper wraps every lazy route so a stale-chunk 404 self-heals
// with EXACTLY ONE guarded reload (fetching the fresh index.html + current
// chunk map), and a genuinely-broken deploy can never reload-loop.
//
// Flow per import:
//   1. import() the chunk.
//   2. on failure, retry once — a transient network blip resolves here, no reload.
//   3. if the retry still fails with a chunk-load error AND this chunk hasn't
//      already triggered a reload this session, arm a per-chunk sessionStorage
//      guard and do ONE full reload. The returned promise never settles so
//      Suspense holds its loading state — the ErrorBoundary never flashes.
//   4. if the guard is already armed (we reloaded once and it STILL fails →
//      broken deploy) or the error isn't a chunk error (a real bug), reject so
//      the ErrorBoundary shows its bilingual fallback + working reload button.
//
// The guard is keyed PER CHUNK and cleared on a successful load, so each
// distinct staleness event self-heals with its own single reload — the earlier
// single-global-key guard only healed the FIRST staleness of a session, which
// is exactly the bug the owner hit across a day of ~20 deploys.

// The browser error messages thrown when a dynamic import() chunk can't be
// fetched — a stale chunk hash after a deploy, or a transient network failure.
// Kept in sync with the pattern the ErrorBoundary used to match.
const CHUNK_LOAD_ERROR =
  /(Loading chunk [\d]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|'?text\/html'? is not a valid JavaScript MIME type)/i;

// Per-chunk guard key prefix. One entry per chunk id lives in sessionStorage
// (survives the reload; scoped to the tab).
export const RELOAD_GUARD_PREFIX = 'tfic-chunk-reload:';

// The module shape a lazy page import resolves to.
type PageModule<T extends ComponentType<any>> = { default: T };

// The minimal storage surface we need — private-browsing / SSR / the node test
// env may not expose a real sessionStorage, so it is nullable and injectable.
type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

// Injected environment so the recovery logic is testable without a DOM.
export interface RetryEnv {
  reload: () => void;
  storage: StorageLike | null;
}

// The real browser environment. Every access is defensive: sessionStorage can
// throw on mere access in locked-down browsers, and window may be absent (SSR).
function browserEnv(): RetryEnv {
  let storage: StorageLike | null = null;
  try {
    storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    storage = null;
  }
  return {
    reload: () => {
      try {
        window.location.reload();
      } catch {
        /* no window (SSR) — nothing to reload */
      }
    },
    storage,
  };
}

// True when an import rejection looks like a fetch/parse failure of the chunk
// itself (stale deploy or network), as opposed to a real error thrown while the
// module evaluates. Only chunk-load errors are worth reloading for.
export function isChunkLoadError(error: unknown): boolean {
  const e = error as { message?: unknown; name?: unknown } | null | undefined;
  const message = `${typeof e?.message === 'string' ? e.message : ''} ${
    typeof e?.name === 'string' ? e.name : ''
  }`;
  return CHUNK_LOAD_ERROR.test(message);
}

function isGuardArmed(env: RetryEnv, key: string): boolean {
  try {
    return env.storage?.getItem(key) === '1';
  } catch {
    return false;
  }
}

function armGuard(env: RetryEnv, key: string): void {
  try {
    env.storage?.setItem(key, '1');
  } catch {
    /* storage unavailable — worst case we skip the reload and show the fallback */
  }
}

function disarmGuard(env: RetryEnv, key: string): void {
  try {
    env.storage?.removeItem(key);
  } catch {
    /* no-op */
  }
}

// The dependency-injected core (see the flow at the top of the file). Exported
// for unit testing; production goes through `lazyRetry`.
export function importWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<PageModule<T>>,
  chunkId: string,
  env: RetryEnv = browserEnv(),
): Promise<PageModule<T>> {
  const key = RELOAD_GUARD_PREFIX + chunkId;

  const onLoaded = (mod: PageModule<T>): PageModule<T> => {
    // The chunk loaded — disarm THIS chunk's guard so a LATER deploy can heal
    // again with a fresh single reload. A genuinely-broken chunk never reaches
    // here, so its guard is never disarmed → it can never reload-loop.
    disarmGuard(env, key);
    return mod;
  };

  const recover = (error: unknown): Promise<PageModule<T>> => {
    if (isChunkLoadError(error) && !isGuardArmed(env, key)) {
      // Stale-deploy 404, first time for this chunk this session → one guarded
      // hard reload. Never settle: hold Suspense's loading state until the
      // reload lands so the error screen can't flash first.
      armGuard(env, key);
      env.reload();
      return new Promise<PageModule<T>>(() => {});
    }
    // Already reloaded once and still failing (broken deploy), or not a chunk
    // error (a real bug) → surface to the ErrorBoundary. Never reload-loop.
    throw error;
  };

  // Retry once before deciding to reload: a transient network blip resolves in
  // the retry with no reload at all.
  return factory().then(onLoaded, () => factory().then(onLoaded, recover));
}

// Wrap a lazy page import so a stale-deploy chunk 404 self-heals instead of
// dead-ending on the error screen. Generic and page-agnostic: every lazy route
// in src/router/config.tsx uses this; page components are untouched.
//   Home = lazyRetry(() => import('../pages/home/page'), 'home')
// `chunkId` need only be stable and unique per chunk (it keys the reload guard).
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<PageModule<T>>,
  chunkId: string,
): LazyExoticComponent<T> {
  return lazy(() => importWithRetry(factory, chunkId));
}
